// src/pages/MiDocumentacion.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    addDoc, 
    deleteDoc, 
    doc, 
    serverTimestamp,
    getDoc 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { 
    FaCloudUploadAlt, 
    FaDownload, 
    FaEye, 
    FaTrashAlt, 
    FaFileAlt,
    FaChevronLeft,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaCheckCircle,
    FaInfoCircle
} from 'react-icons/fa';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import ConfirmationModal from '../components/ConfirmationModal';
import ConfirmDialog from '../components/ConfirmDialog';
import './MiDocumentacion.css';

const MiDocumentacion = () => {
    const { casaId } = useParams();
    const navigate = useNavigate();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [casaData, setCasaData] = useState(null);
    const fileInputRef = useRef(null);
    const [notificationModal, setNotificationModal] = useState({
        open: false,
        type: 'info',
        title: '',
        message: '',
        icon: null
    });
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({
        open: false,
        documento: null
    });

    // Cargar datos de la casa
    useEffect(() => {
        if (!fraccionamientoId || !casaId) return;

        const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId);
        const unsubscribe = onSnapshot(casaRef, (snap) => {
            if (snap.exists()) {
                setCasaData({ id: snap.id, ...snap.data() });
            }
        }, (error) => {
            console.error('Error cargando datos de la casa:', error);
        });

        return () => unsubscribe();
    }, [fraccionamientoId, casaId]);

    // Cargar documentos
    useEffect(() => {
        if (!fraccionamientoId || !casaId) {
            setLoading(false);
            return;
        }

        const documentosRef = collection(
            db, 
            'fraccionamientos', 
            fraccionamientoId, 
            'casas', 
            casaId, 
            'documentacion'
        );
        
        const q = query(documentosRef, where('casaId', '==', casaId));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).sort((a, b) => {
                // Ordenar por fecha de creación descendente
                const aTime = a.fechaCreacion?.toMillis?.() || 0;
                const bTime = b.fechaCreacion?.toMillis?.() || 0;
                return bTime - aTime;
            });
            
            setDocumentos(docsList);
            setLoading(false);
        }, (error) => {
            console.error('Error cargando documentos:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId, casaId]);

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) { // 10MB límite
            setNotificationModal({
                open: true,
                type: 'warning',
                title: 'Archivo demasiado grande',
                message: 'El archivo es demasiado grande. El tamaño máximo permitido es 10MB.',
                icon: <FaExclamationTriangle />
            });
            return;
        }

        setUploading(true);
        try {
            // Subir archivo a Storage
            const timestamp = Date.now();
            const fileName = `${casaId}_${timestamp}_${file.name}`;
            const storagePath = `fraccionamientos/${fraccionamientoId}/casas/${casaId}/documentacion/${fileName}`;
            const fileRef = ref(storage, storagePath);

            await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(fileRef);

            // Guardar metadata en Firestore
            await addDoc(
                collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId, 'documentacion'),
                {
                    casaId: casaId,
                    nombre: file.name,
                    nombreArchivo: fileName,
                    url: downloadURL,
                    tipo: file.type,
                    tamaño: file.size,
                    fechaCreacion: serverTimestamp(),
                    subidoPor: userData?.email || userData?.uid || 'admin',
                    fraccionamientoId: fraccionamientoId
                }
            );

            // Registrar movimiento
            if (casaData) {
                await registerMovement({
                    fraccionamientoId,
                    casaId,
                    tipoMovimiento: 'documentacion',
                    categoria: 'administracion',
                    titulo: 'Documento cargado',
                    descripcion: `Se cargó el documento: ${file.name}`,
                    monto: 0,
                    origen: 'admin'
                });
            }

            setNotificationModal({
                open: true,
                type: 'success',
                title: 'Documento cargado',
                message: 'El documento se ha cargado exitosamente.',
                icon: <FaCheckCircle />
            });
        } catch (error) {
            console.error('Error subiendo documento:', error);
            setNotificationModal({
                open: true,
                type: 'error',
                title: 'Error al cargar',
                message: 'Error al cargar el documento. Intenta nuevamente.',
                icon: <FaExclamationTriangle />
            });
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDeleteClick = (documento) => {
        setDeleteConfirmModal({
            open: true,
            documento: documento
        });
    };

    const handleDelete = async (documento) => {
        if (!documento) return;

        setDeletingId(documento.id);
        try {
            // Eliminar archivo de Storage
            if (documento.nombreArchivo) {
                const storagePath = `fraccionamientos/${fraccionamientoId}/casas/${casaId}/documentacion/${documento.nombreArchivo}`;
                const fileRef = ref(storage, storagePath);
                await deleteObject(fileRef);
            }

            // Eliminar documento de Firestore
            await deleteDoc(
                doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId, 'documentacion', documento.id)
            );

            // Registrar movimiento
            if (casaData) {
                await registerMovement({
                    fraccionamientoId,
                    casaId,
                    tipoMovimiento: 'documentacion',
                    categoria: 'administracion',
                    titulo: 'Documento eliminado',
                    descripcion: `Se eliminó el documento: ${documento.nombre}`,
                    monto: 0,
                    origen: 'admin'
                });
            }

            setDeleteConfirmModal({ open: false, documento: null });
            setNotificationModal({
                open: true,
                type: 'success',
                title: 'Documento eliminado',
                message: 'El documento se ha eliminado exitosamente.',
                icon: <FaCheckCircle />
            });
        } catch (error) {
            console.error('Error eliminando documento:', error);
            setNotificationModal({
                open: true,
                type: 'error',
                title: 'Error al eliminar',
                message: 'Error al eliminar el documento. Intenta nuevamente.',
                icon: <FaExclamationTriangle />
            });
        } finally {
            setDeletingId(null);
        }
    };

    const handleView = (documento) => {
        setViewingDoc(documento);
    };

    const handleDownload = async (documento) => {
        try {
            const response = await fetch(documento.url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = documento.nombre;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error descargando documento:', error);
            setNotificationModal({
                open: true,
                type: 'error',
                title: 'Error al descargar',
                message: 'Error al descargar el documento. Intenta nuevamente.',
                icon: <FaExclamationTriangle />
            });
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Fecha no disponible';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFileIcon = (tipo) => {
        if (!tipo) return <FaFileAlt />;
        if (tipo.includes('pdf')) return <FaFileAlt style={{ color: '#dc2626' }} />;
        if (tipo.includes('image')) return <FaFileAlt style={{ color: '#3b82f6' }} />;
        if (tipo.includes('word') || tipo.includes('document')) return <FaFileAlt style={{ color: '#2563eb' }} />;
        if (tipo.includes('excel') || tipo.includes('spreadsheet')) return <FaFileAlt style={{ color: '#16a34a' }} />;
        return <FaFileAlt />;
    };

    const numeroCasa = casaData?.numero || casaData?.numeroCasa || casaId;
    const nombreResidente = casaData?.nombre_residente || casaData?.nombre || casaData?.propietario || 'Residente';

    return (
        <div className="mi-documentacion-page">
            <header className="mi-documentacion-header">
                <div className="mi-documentacion-header-content">
                    <div className="mi-documentacion-header-icon">
                        <FaFileAlt />
                    </div>
                    <div>
                        <h1>Mi Documentación</h1>
                        <p>Gestiona los documentos de {nombreResidente} - Casa {numeroCasa}</p>
                    </div>
                </div>
                <button 
                    className="mi-documentacion-back-link" 
                    onClick={() => navigate(-1)}
                >
                    <FaChevronLeft /> Volver
                </button>
            </header>

            <div className="mi-documentacion-content">
                <div className="mi-documentacion-toolbar">
                    <div className="mi-documentacion-upload-section">
                        <input
                            ref={fileInputRef}
                            type="file"
                            id="file-upload"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                        />
                        <button
                            className="mi-documentacion-upload-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <FaSpinner className="spinning" /> Subiendo...
                                </>
                            ) : (
                                <>
                                    <FaCloudUploadAlt /> Subir Documento
                                </>
                            )}
                        </button>
                        <p className="mi-documentacion-upload-hint">
                            Formatos permitidos: PDF, Word, Excel, Imágenes. Tamaño máximo: 10MB
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="mi-documentacion-loading">
                        <FaSpinner className="spinning" /> Cargando documentos...
                    </div>
                ) : documentos.length === 0 ? (
                    <div className="mi-documentacion-empty">
                        <FaFileAlt />
                        <p>No hay documentos cargados aún</p>
                        <p className="mi-documentacion-empty-hint">
                            Usa el botón "Subir Documento" para agregar documentos de este residente
                        </p>
                    </div>
                ) : (
                    <div className="mi-documentacion-list">
                        {documentos.map((doc) => (
                            <div key={doc.id} className="mi-documentacion-item">
                                <div className="mi-documentacion-item-icon">
                                    {getFileIcon(doc.tipo)}
                                </div>
                                <div className="mi-documentacion-item-info">
                                    <h3 className="mi-documentacion-item-title">{doc.nombre}</h3>
                                    <div className="mi-documentacion-item-meta">
                                        <span>{formatFileSize(doc.tamaño || 0)}</span>
                                        <span>•</span>
                                        <span>{formatDate(doc.fechaCreacion)}</span>
                                    </div>
                                </div>
                                <div className="mi-documentacion-item-actions">
                                    <button
                                        className="mi-documentacion-action-btn view"
                                        onClick={() => handleView(doc)}
                                        title="Ver documento"
                                    >
                                        <FaEye />
                                    </button>
                                    <button
                                        className="mi-documentacion-action-btn download"
                                        onClick={() => handleDownload(doc)}
                                        title="Descargar documento"
                                    >
                                        <FaDownload />
                                    </button>
                                    <button
                                        className="mi-documentacion-action-btn delete"
                                        onClick={() => handleDeleteClick(doc)}
                                        disabled={deletingId === doc.id}
                                        title="Eliminar documento"
                                    >
                                        {deletingId === doc.id ? (
                                            <FaSpinner className="spinning" />
                                        ) : (
                                            <FaTrashAlt />
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal para ver documento */}
            {viewingDoc && (
                <div className="mi-documentacion-modal-overlay" onClick={() => setViewingDoc(null)}>
                    <div className="mi-documentacion-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="mi-documentacion-modal-header">
                            <h2>{viewingDoc.nombre}</h2>
                            <button
                                className="mi-documentacion-modal-close"
                                onClick={() => setViewingDoc(null)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="mi-documentacion-modal-body">
                            {viewingDoc.tipo?.includes('image') ? (
                                <img 
                                    src={viewingDoc.url} 
                                    alt={viewingDoc.nombre}
                                    style={{ maxWidth: '100%', maxHeight: '80vh' }}
                                />
                            ) : (
                                <iframe
                                    src={viewingDoc.url}
                                    style={{
                                        width: '100%',
                                        height: '80vh',
                                        border: 'none',
                                        borderRadius: '8px'
                                    }}
                                    title={viewingDoc.nombre}
                                />
                            )}
                        </div>
                        <div className="mi-documentacion-modal-footer">
                            <button
                                className="mi-documentacion-modal-btn"
                                onClick={() => handleDownload(viewingDoc)}
                            >
                                <FaDownload /> Descargar
                            </button>
                            <button
                                className="mi-documentacion-modal-btn secondary"
                                onClick={() => setViewingDoc(null)}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de notificación (éxito/error/info) */}
            <ConfirmationModal
                open={notificationModal.open}
                type={notificationModal.type}
                title={notificationModal.title}
                message={notificationModal.message}
                icon={notificationModal.icon}
                onClose={() => setNotificationModal({ ...notificationModal, open: false })}
            />

            {/* Modal de confirmación para eliminar */}
            <ConfirmDialog
                open={deleteConfirmModal.open}
                title="Eliminar documento"
                message={deleteConfirmModal.documento ? `¿Estás seguro de que deseas eliminar el documento "${deleteConfirmModal.documento.nombre}"? Esta acción no se puede deshacer.` : ''}
                confirmLabel="Eliminar"
                cancelLabel="Cancelar"
                tone="danger"
                onConfirm={() => handleDelete(deleteConfirmModal.documento)}
                onCancel={() => setDeleteConfirmModal({ open: false, documento: null })}
            />
        </div>
    );
};

export default MiDocumentacion;

